/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionRemoveURLParam';

const ActionRemoveURLParamManager: ActionManagerFn<'ActionRemoveURLParam'> = (
  platform: Platform,
): ActionManager<'ActionRemoveURLParam'> => {
  return {
    ...baseActionManager(platform, 'ActionRemoveURLParam'),
    defaultValue,
    props: {
      ...baseActionProps,
      key: {
        label: 'Name',
        type: 'expression',
        required: true,
        returnType: async () => 'string',
      },
    },
    name: 'Remove URL Param',
    getDisplayText: (item) =>
      typeof item.props.key === 'string'
        ? item.props.key
        : item.props.key.value,
    description: `**Overview**<br/>
The Remove URL Param action is a routing utility component designed to modify the current browser URL by deleting a specific query parameter. It allows the application to clean up the address bar dynamically without triggering a full page reload or navigating away from the current view.<br/><br/>
This action is typically used to manage application state that is reflected in the URL. Once a parameter has served its purpose (like triggering an initial popup or passing a success token), this action removes it to prevent the same logic from running again if the user refreshes the page or bookmarks the URL.

**Key Features**<br/>
* **Targeted Cleanup:** Specifically identifies and removes a single parameter key from the query string (e.g., changing ?page=dashboard&action=success to ?page=dashboard), leaving other parameters intact.
* **Dynamic Keys:** The key property supports expressions, allowing developers to programmatically determine which parameter to remove based on runtime conditions.
* **State Reset:** Essential for "one-time" actions, ensuring that temporary flags passed via the URL do not persist longer than necessary.

**Properties**<br/>
* **Name (key) (Required):** An expression property returning a string.
  * *Function:* The specific name of the URL query parameter to be removed (e.g., 'recordId' or 'status').

**Use Cases**<br/>
* **Post-Action Cleanup:** A user arrives at a page via a link .../home?msg=saved. The app reads the msg param, shows a "Saved Successfully" toast, and then immediately uses this action to remove msg so the toast doesn't appear again on refresh.
* **Modal Management:** A URL parameter openId=123 opens a specific modal on load. When the user closes the modal, this action removes openId from the URL to reflect the new state.
* **Security:** Removing sensitive tokens or temporary access keys from the URL bar immediately after they have been processed by the application.`,
  };
};

export default ActionRemoveURLParamManager;
